import type { MeshStandardMaterial } from 'three';
import type { CellMesh } from './cells.js';

export interface Tween {
  update(dt: number): boolean;
}

/**
 * The CBLIX2 signature animation: a cell physically FLIPS OVER
 * 180° around its X-axis to reveal a new colour on the back face.
 * This is the identity of the game. Don't replace it with a fade or pop.
 */
export const createFlipTween = (
  mesh: CellMesh,
  fromMat: MeshStandardMaterial,
  toMat: MeshStandardMaterial,
  delay = 0,
): Tween => {
  const dur = 0.55;
  let t = -delay;
  const baseZ = mesh.userData.baseZ;
  let swapped = false;
  return {
    update(dt) {
      t += dt;
      if (t < 0) return true;
      if (t >= dur) {
        mesh.rotation.x = 0;
        mesh.position.z = baseZ;
        mesh.material = toMat;
        return false;
      }
      const k = t / dur;
      const ease = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      mesh.rotation.x = ease * Math.PI;
      mesh.position.z = baseZ + Math.sin(k * Math.PI) * 0.35;
      if (!swapped && k >= 0.5) {
        mesh.material = toMat;
        swapped = true;
      }
      if (k < 0.5) mesh.material = fromMat;
      return true;
    },
  };
};

export const createPopTween = (mesh: CellMesh, delay = 0, intensity = 1): Tween => {
  const dur = 0.45;
  let t = -delay;
  const baseZ = mesh.userData.baseZ;
  const peakZ = baseZ + 0.4 * intensity;
  return {
    update(dt) {
      t += dt;
      if (t < 0) return true;
      if (t >= dur) {
        mesh.position.z = baseZ;
        mesh.scale.set(1, 1, 1);
        return false;
      }
      const k = t / dur;
      const ease = Math.sin(k * Math.PI);
      mesh.position.z = baseZ + ease * (peakZ - baseZ);
      const s = 1 + ease * 0.18 * intensity;
      mesh.scale.set(s, s, 1 + ease * 0.55 * intensity);
      return true;
    },
  };
};

export const createWobbleTween = (mesh: CellMesh, delay = 0): Tween => {
  const dur = 0.6;
  let t = -delay;
  const baseZ = mesh.userData.baseZ;
  return {
    update(dt) {
      t += dt;
      if (t < 0) return true;
      if (t >= dur) {
        mesh.rotation.set(0, 0, 0);
        mesh.position.z = baseZ;
        return false;
      }
      const k = t / dur;
      const wobble = Math.sin(k * Math.PI * 4) * (1 - k);
      mesh.rotation.z = wobble * 0.18;
      mesh.position.z = baseZ + Math.abs(wobble) * 0.2;
      return true;
    },
  };
};

export interface CancellableTween extends Tween {
  cancel(): void;
}

/**
 * Continuous flip loop for flipping cells. Every `period` seconds it
 * runs a 180° X-axis flip while swapping between matA and matB.
 * Cancellable: when the cell freezes, call cancel() to stop the loop.
 */
export const createFlipLoopTween = (
  mesh: CellMesh,
  matA: MeshStandardMaterial,
  matB: MeshStandardMaterial,
  period = 2.6,
  startOffset = 0,
): CancellableTween => {
  const flipDur = 0.55;
  let t = startOffset;
  let mode: 'idle' | 'flip' = 'idle';
  let flipT = 0;
  let showingA = true;
  let cancelled = false;
  return {
    update(dt) {
      if (cancelled) {
        mesh.rotation.x = 0;
        mesh.material = showingA ? matA : matB;
        return false;
      }
      if (mode === 'idle') {
        t += dt;
        mesh.material = showingA ? matA : matB;
        if (t >= period) {
          mode = 'flip';
          flipT = 0;
        }
        return true;
      }
      flipT += dt;
      if (flipT >= flipDur) {
        showingA = !showingA;
        mesh.rotation.x = 0;
        mesh.material = showingA ? matA : matB;
        mode = 'idle';
        t = 0;
        return true;
      }
      const k = flipT / flipDur;
      mesh.rotation.x = k * Math.PI;
      mesh.material = k < 0.5 ? (showingA ? matA : matB) : showingA ? matB : matA;
      return true;
    },
    cancel() {
      cancelled = true;
    },
  };
};

export class TweenSet {
  private active: Tween[] = [];

  add(t: Tween): void {
    this.active.push(t);
  }

  step(dt: number): void {
    this.active = this.active.filter((t) => t.update(dt));
  }

  clear(): void {
    this.active = [];
  }

  get count(): number {
    return this.active.length;
  }
}
